import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { _equal } from '@shared/aux_helper_functions';
import { GenericTagsSelectFilteredService } from './generic-tags-select-filtered.service';
import { _log } from '@shared/aux_helper_environment';
export interface IFilter {
  tagId: number;
  tagValues: number[];
  order?: number;
}

@Component({
  selector: 'generic-tags-select-filtered',
  template: `
    <div class="generic-tags-select-filtered-root">
      <ng-container *ngIf="isLoading">
        <div class="spinner-container-mini">
          <custom-loader></custom-loader>
        </div>
      </ng-container>
      <div
        [ngStyle]="{ opacity: isLoading ? '0.5' : '1', filter: isLoading ? 'blur(1px)' : 'none' }"
        class="generic-tags-select-filtered-content"
      >
        <generic-tags-select-multiple-v2
          *ngIf="isDataReady"
          [useAsAdvancedFilter]="useAsAdvancedFilter"
          [autoVisibleTagsRoots]="autoVisibleTagsRoots"
          [tagsModuleType]="tagsModuleType"
          [selectedTags]="selectedTags"
          [configLevels]="configLevels"
          [autoFillParentsOnStart]="autoFillParentsOnStart"
          [deafaultParentClass]="deafaultParentClass"
          (onChangeGroup)="changeFilterGenericTagSelect($event)"
          (openedChange)="onOpenedStoreTagsChange($event)"
          [visibleTagsValuesIds]="filteredTagValuesIds"
          [tmpFix_PROM_397_markForCheck]="true"
          (initialValues)="setInitialValues($event)"
          [visibleTagsRoots]="visibleTagsRoots"
          [orderPrefix]="_getOrderFilters"
        >
        </generic-tags-select-multiple-v2>
      </div>
    </div>
  `,
  styles: [
    `
      .generic-tags-select-filtered-root {
        display: flex;
        flex-direction: column;
        gap: 10px;
        position: relative;
        transition: opacity 0.3s ease, filter 0.3s ease;
      }
      .spinner-container-mini {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
      }
      .generic-tags-select-filtered-content {
        transition: opacity 0.3s ease, filter 0.3s ease;
        z-index: 1;
      }
    `,
  ],
})
export class GenericTagsSelectFilteredComponent implements OnInit {
  @Input() useAsAdvancedFilter: boolean;
  @Input() autoVisibleTagsRoots: boolean;
  @Input() tagsModuleType: number;
  @Input() selectedTags: any[];
  @Input() configLevels: any;
  @Input() autoFillParentsOnStart: boolean;
  @Input() deafaultParentClass: string;
  @Output() onChangeGroup: EventEmitter<any> = new EventEmitter();
  @Output() onCloseSelect: EventEmitter<any> = new EventEmitter();
  @Input() filters: IFilter[] = [];
  @Input() visibleTagsRoots: { id: number; isRequired?: boolean }[] = null;
  @Output() isLoadingChange: EventEmitter<boolean> = new EventEmitter();
  filteredTagValuesIds: number[] = null;
  isDataReady = false;

  private _filters: IFilter[] = [];
  originalFilters: IFilter[] = [];
  isLoading = false;
  initialValues: number[] = [];

  private nextOrderNumber = 1;

  constructor(private genericTagsSelectFilteredService: GenericTagsSelectFilteredService) {}

  ngOnInit(): void {
    if (this.filters?.length > 0) {
      this._filters = this.filters;
      // Inicializar filtros con order null
      this._filters = this.filters.map(filter => ({
        ...filter,
        order: filter.order || null,
      }));
    } else {
      // Asegurar que _filters esté inicializado como array vacío
      this._filters = [];
    }

    this.getFilteredTagValuesIds();
  }

  onOpenedStoreTagsChange($event) {
    if ($event === false) this.onCloseSelect.emit(this._filters);
    if ($event === false && this._filters.length > 0 && !_equal(this._filters, this.originalFilters)) {
      this.getFilteredTagValuesIds();
    }
    this.originalFilters = this._filters;
  }

  getFilteredTagValuesIds() {
    this.isLoadingChange.emit(true);
    this.isLoading = true;
    this.configLevels = { ...this.configLevels, disabled: true };
    this.genericTagsSelectFilteredService.getFilteredTags(this._filters, this.tagsModuleType).subscribe(data => {
      this.filteredTagValuesIds = data?.filteredTagValueIds || [];
      this.visibleTagsRoots = data?.visibleTagsRoots || null;
      if (!(this.visibleTagsRoots?.length > 0)) this.autoVisibleTagsRoots = true;

      this.isLoading = false;
      // si elimina todos los tags, se mantienen los tags iniciales
      if (!this._filters.some(item => item.tagValues.length > 0) && this.initialValues?.length > 0) {
        this.filteredTagValuesIds = this.initialValues;
      }
      this.configLevels = { ...this.configLevels, disabled: false };
      this.isLoadingChange.emit(false);
    });
    this.isDataReady = true;
  }

  setInitialValues(event: number[]) {
    this.initialValues = event;
  }

  /**
   * Método que actualiza la propiedad order de los filtros basándose en cuando se agregan o eliminan valores
   * @param newFilters Los nuevos filtros
   * @param previousFilters Los filtros anteriores para comparar
   */
  private updateFiltersOrder(newFilters: IFilter[], previousFilters: IFilter[]): IFilter[] {
    const updatedFilters = newFilters.map(newFilter => {
      const previousFilter = previousFilters.find(pf => pf.tagId === newFilter.tagId);

      // Si el filtro no existía antes, inicializar con order null
      if (!previousFilter) {
        const hasValuesNow = newFilter.tagValues && newFilter.tagValues.length > 0;
        if (hasValuesNow) {
          return { ...newFilter, order: -1 }; // Temporal, se reorganizará después
        } else {
          return { ...newFilter, order: null };
        }
      }

      const hasValuesNow = newFilter.tagValues && newFilter.tagValues.length > 0;
      const hadValuesBefore = previousFilter.tagValues && previousFilter.tagValues.length > 0;

      // Si antes no tenía valores y ahora sí, marcar para reorganizar
      if (!hadValuesBefore && hasValuesNow) {
        return { ...newFilter, order: -1 }; // Temporal, se reorganizará después
      }

      // Si antes tenía valores y ahora no, quitar order
      if (hadValuesBefore && !hasValuesNow) {
        return { ...newFilter, order: null };
      }

      // Si ya tenía valores antes, mantener el order existente temporalmente
      if (hadValuesBefore && hasValuesNow && previousFilter.order !== null && previousFilter.order !== undefined) {
        return { ...newFilter, order: previousFilter.order };
      }

      // Por defecto, mantener order como null
      return { ...newFilter, order: null };
    });

    // Reorganizar los orders para que sean consecutivos empezando desde 1
    const reorganizedFilters = this.reorganizeOrders(updatedFilters);

    return reorganizedFilters;
  }

  /**
   * Reorganiza los orders para que sean consecutivos empezando desde 1
   */
  private reorganizeOrders(filters: IFilter[]): IFilter[] {
    // Separar filtros con valores de los sin valores
    const filtersWithValues = filters.filter(f => f.tagValues && f.tagValues.length > 0);
    const filtersWithoutValues = filters.filter(f => !f.tagValues || f.tagValues.length === 0);

    // Ordenar los filtros con valores por su order actual (los -1 van al final)
    filtersWithValues.sort((a, b) => {
      if (a.order === -1 && b.order !== -1) return 1;
      if (a.order !== -1 && b.order === -1) return -1;
      if (a.order === -1 && b.order === -1) return 0;
      return (a.order || 0) - (b.order || 0);
    });

    // Asignar orders consecutivos empezando desde 1
    const reorganizedFilters = filters.map(filter => {
      const hasValues = filter.tagValues && filter.tagValues.length > 0;
      if (hasValues) {
        const index = filtersWithValues.findIndex(f => f.tagId === filter.tagId);
        const newOrder = index + 1;
        return { ...filter, order: newOrder };
      } else {
        return { ...filter, order: null };
      }
    });

    // Actualizar nextOrderNumber para el próximo filtro nuevo
    this.nextOrderNumber = filtersWithValues.length + 1;

    return reorganizedFilters;
  }

  changeFilterGenericTagSelect(event) {
    // Actualizar el order antes de asignar los nuevos filtros
    const updatedFilters = this.updateFiltersOrder(event, this._filters);
    this.onChangeGroup.emit(updatedFilters);
    this._filters = updatedFilters;
  }

  /**
   * Método público para obtener los filtros ordenados por su propiedad order
   * @returns Array de filtros ordenados por order (los que tienen order null van al final)
   */
  public getFiltersOrderedBySelection(): IFilter[] {
    return [...this._filters].sort((a, b) => {
      // Si ambos tienen order, ordenar por order
      if (a.order !== null && a.order !== undefined && b.order !== null && b.order !== undefined) {
        return a.order - b.order;
      }
      // Si solo a tiene order, a va primero
      if (a.order !== null && a.order !== undefined && (b.order === null || b.order === undefined)) {
        return -1;
      }
      // Si solo b tiene order, b va primero
      if ((a.order === null || a.order === undefined) && b.order !== null && b.order !== undefined) {
        return 1;
      }
      // Si ninguno tiene order, mantener orden original
      return 0;
    });
  }

  /**
   * Método público para obtener los filtros actuales con order
   */
  public getCurrentFiltersWithOrder(): IFilter[] {
    return [...this._filters];
  }

  /**
   * Método público para resetear la numeración del order
   */
  public resetOrderNumbering(): void {
    this.nextOrderNumber = 1;
    this._filters = this._filters.map(filter => ({
      ...filter,
      order: filter.tagValues && filter.tagValues.length > 0 ? this.nextOrderNumber++ : null,
    }));
  }

  get _getOrderFilters() {
    if (!(this._filters?.length > 0)) return null;
    return this._filters
      .filter(filter => filter.order !== null)
      .map(filter => ({
        tagId: filter.tagId,
        order: filter.order,
      }));
  }

  public visibleTagsValuesIds_Dummy = [
    1159, 4969, 39545, 3564, 40143, 39540, 40093, 16931, 8924, 7572, 12907, 42740, 4963, 14257, 41344, 40517, 10257, 1, 1683, 85, 22207,
    40326, 39960, 42627, 41580, 40387, 3558, 6400, 15605, 42966, 11602, 381, 2275, 22061, 19338, 6346, 20604, 8975, 44466, 44469, 44465,
    44461, 44462,
  ];
}
